import { FC } from "react";
import { Alert, Button, PageHeader, Spinner } from "@justworkshr/milo-core";
import { ActionFooter } from "@justworkshr/milo-form";
import { useGetEoRMemberBackgroundCheckQuery } from "types/generated/operations";
import useOnboardingForm from "../hooks/useOnboardingForm";
import Layout from "../Layout";
import LaunchBackgroundCheck from "./LaunchBackgroundCheck";
import BackgroundCheckStatus from "./BackgroundCheckStatus";
import onboardingStyles from "../Onboarding.module.css";
import { useTranslation } from "react-i18next";

const BackgroundCheck: FC = () => {
  const { t } = useTranslation();
  const {
    loading,
    memberData: { memberId },
    form: {
      submitting,
      navigatingBack,
      showErrorAlert,
      errorAlertMessage,
      onSubmit,
      onBack,
    },
    memberOnboarding: { stepNumber, redirecting, backgroundCheckStatus },
  } = useOnboardingForm("background-check");

  const { data: backgroundCheckData } = useGetEoRMemberBackgroundCheckQuery({
    variables: {
      memberId: memberId as string,
    },
    skip: !memberId,
  });

  const isBackgroundCheckInputComplete = !backgroundCheckData
    ?.backgroundCheckStatus?.status
    ? undefined
    : !["requested"].includes(backgroundCheckData.backgroundCheckStatus.status);

  const displayCard = () => {
    if (isBackgroundCheckInputComplete === undefined) {
      return <Spinner />;
    }

    return isBackgroundCheckInputComplete ? (
      <BackgroundCheckStatus
        status={backgroundCheckData?.backgroundCheckStatus?.status as string}
      />
    ) : (
      <LaunchBackgroundCheck memberId={memberId || ""} onSubmit={onSubmit} />
    );
  };

  return (
    <Layout
      step={stepNumber}
      loading={loading || redirecting}
      backgroundCheckStatus={backgroundCheckStatus}
    >
      <Alert color="destructive" visible={showErrorAlert}>
        {t(errorAlertMessage) ||
          t("An error occurred while submitting your background check.")}
      </Alert>

      <PageHeader title={t("Background Check")} />

      {displayCard()}

      <ActionFooter
        className={onboardingStyles.footer}
        actions={[
          <Button
            color="brand"
            variant="filled"
            onClick={() => onSubmit({})}
            loading={submitting}
            disabled={navigatingBack || !isBackgroundCheckInputComplete}
          >
            {t("Continue")}
          </Button>,
        ]}
        secondary={[
          <Button
            type="button"
            variant="outlined"
            color="brand"
            key="back-btn"
            loading={navigatingBack}
            onClick={onBack}
            disabled={submitting}
          >
            {t("Go back")}
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default BackgroundCheck;
